import React from 'react';
import classNames from 'classnames';

import { useSelector } from 'react-redux';
import { asObject } from '../../../common/utils/immutableHelper';

import FloatingPanel from '../../components/tooltips/FloatingPanel';
import FloatingArrow from '../../components/tooltips/FloatingArrow';

import { OnboardingFloatingArrowConfig, OnboardingStep, OnboardingTooltipConfig } from './config/onboardingConfigTypes';

import './WorkspaceOnboardingManager.scss';

const selectCurrentStep = (state: any) => state.getIn(['app', 'onboarding', 'currentStep']);

const createDisableStylesheet = (disableSelectors: string[]) => ({
    __html: disableSelectors
        .map((selector) => `${selector} { pointer-events: none; opacity: ${0.3}; * { pointer-events: none; } }`)
        .join('\n'),
});

const getFloatingPanel = (stepId: string, tooltip: OnboardingTooltipConfig | undefined) => {
    if (!tooltip) return null;

    // no point in rendering tooltip if there is no content
    const TooltipComponent = tooltip.component;
    if (!TooltipComponent) return null;

    // at this point, selector has been resolved by onboardingMiddleware as a string
    const selector = tooltip.selector as string;

    const className = classNames('WorkspaceOnboardingFloatingPanel', tooltip.className);

    return (
        <FloatingPanel
            mountPointSelector="#app .Workspace.workspace-ready"
            key={`${stepId}-${selector}`}
            selector={selector}
            position={tooltip.position}
            offset={tooltip.offset}
            delay={tooltip.delay}
            className={className}
            style={tooltip.style}
            arrowStyle={tooltip.arrowStyle}
            pollPosition
        >
            <TooltipComponent key={`${selector}-tooltip`} />
        </FloatingPanel>
    );
};

const getFloatingArrow = (arrowProps: OnboardingFloatingArrowConfig | undefined) => {
    if (!arrowProps) return null;

    return (
        <FloatingArrow
            selector={arrowProps.selector}
            fallbackSelector={arrowProps.fallbackSelector}
            position={arrowProps.position}
            distance={arrowProps.distance}
            animate={arrowProps.animate}
            delay={arrowProps.delay}
        />
    );
};

const WorkspaceOnboardingManager: React.FC = () => {
    const currentStepImmutable = useSelector(selectCurrentStep);

    if (!currentStepImmutable) return null;
    const currentStep = asObject(currentStepImmutable) as OnboardingStep;

    const tooltip = currentStep.tip;
    const arrowProps = currentStep.arrow;

    return (
        <div className="WorkspaceOnboarding">
            {currentStep.disable?.selectors ? (
                <style dangerouslySetInnerHTML={createDisableStylesheet(currentStep.disable?.selectors)} />
            ) : null}

            {getFloatingPanel(currentStep.id, tooltip)}

            {getFloatingArrow(arrowProps)}
        </div>
    );
};

export default WorkspaceOnboardingManager;
